import React, { useState, useEffect } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonItem, IonInput, IonButton, IonSpinner, IonText,
  IonCard, IonCardHeader, IonCardTitle, IonCardContent,
} from '@ionic/react';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { useGames } from '../hooks/useGames';
import { useHistory } from 'react-router-dom';

const GAME_ICONS: Record<string, string> = {
  pokemon: '\u26A1',
  yugioh: '\uD83D\uDC09',
  mtg: '\uD83E\uDDD9',
  sports: '\uD83C\uDFC8',
  custom: '\uD83E\uDDE9',
};

const Settings: React.FC = () => {
  const { user, setUser, logout } = useAuthStore();
  const history = useHistory();
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { data: allGames = [], isLoading: gamesLoading } = useGames();
  const [selectedGames, setSelectedGames] = useState<number[]>(user?.game_preferences || []);
  const [gamesLoading2, setGamesLoading2] = useState(false);
  const [gamesMessage, setGamesMessage] = useState('');

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setUsername(user.username);
      setSelectedGames(user.game_preferences || []);
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    setMessage('');
    try {
      const updated = await authService.updateProfile({ display_name: displayName, username });
      setUser(updated);
      setMessage('Profile updated!');
    } catch (err: any) {
      setMessage(err.response?.data?.detail || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleGame = (gameId: number) => {
    setSelectedGames((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId]
    );
  };

  const handleSaveGames = async () => {
    setGamesLoading2(true);
    setGamesMessage('');
    try {
      const updated = await authService.updateProfile({ game_preferences: selectedGames });
      setUser(updated);
      setGamesMessage('Game preferences saved!');
    } catch (err: any) {
      setGamesMessage(err.response?.data?.detail || 'Save failed');
    } finally {
      setGamesLoading2(false);
    }
  };

  const handleLogout = () => {
    logout();
    history.push('/login');
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>Settings</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Profile</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonItem>
              <IonInput label="Email" labelPlacement="stacked" value={user?.email} disabled />
            </IonItem>
            <IonItem>
              <IonInput label="Username" labelPlacement="stacked" value={username} onIonInput={(e) => setUsername(e.detail.value || '')} />
            </IonItem>
            <IonItem>
              <IonInput label="Display Name" labelPlacement="stacked" value={displayName} onIonInput={(e) => setDisplayName(e.detail.value || '')} />
            </IonItem>
            {message && <IonText color={message.includes('failed') ? 'danger' : 'success'}><p style={{ padding: '0 16px' }}>{message}</p></IonText>}
            <IonButton expand="block" onClick={handleSave} disabled={loading} style={{ marginTop: 16 }}>
              {loading ? <IonSpinner name="crescent" /> : 'Save Changes'}
            </IonButton>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>My Games</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <p style={{ marginBottom: 12, color: 'var(--ion-color-medium)', fontSize: 14 }}>
              Select the games you collect. Card and binder forms will only show your selected games.
            </p>
            {gamesLoading ? (
              <IonSpinner name="crescent" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allGames.map((game) => (
                  <label key={game.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 16 }}>
                    <input
                      type="checkbox"
                      checked={selectedGames.includes(game.id)}
                      onChange={() => toggleGame(game.id)}
                      style={{ width: 18, height: 18 }}
                    />
                    {GAME_ICONS[game.slug] || ''} {game.name}
                  </label>
                ))}
              </div>
            )}
            {gamesMessage && <IonText color={gamesMessage.includes('failed') ? 'danger' : 'success'}><p style={{ padding: '8px 0 0' }}>{gamesMessage}</p></IonText>}
            <IonButton expand="block" onClick={handleSaveGames} disabled={gamesLoading2} style={{ marginTop: 16 }}>
              {gamesLoading2 ? <IonSpinner name="crescent" /> : 'Save Game Preferences'}
            </IonButton>
          </IonCardContent>
        </IonCard>

        <IonButton expand="block" color="danger" fill="outline" onClick={handleLogout} style={{ marginTop: 24 }}>
          Logout
        </IonButton>
      </IonContent>
    </IonPage>
  );
};

export default Settings;
